import React from "react";
import { Plane } from "lucide-react";

// Simplified world continent silhouettes (stylised).
// Paths sourced from public-domain low-res world data, redrawn minimally.
// Viewbox uses equirectangular projection: x = (lon + 180), y = (90 - lat).
// So to place a pin from lat/lon: x = lon + 180, y = 90 - lat.

const DESTINATIONS = [
    { code: "NG", city: "Lagos", lat: 6.5, lon: 3.4, region: "Africa" },
    { code: "AE", city: "Dubai", lat: 25.2, lon: 55.3, region: "MENA" },
    { code: "GB", city: "London", lat: 51.5, lon: -0.1, region: "EU / UK" },
    { code: "RU", city: "Moscow", lat: 55.75, lon: 37.6, region: "CIS" },
    { code: "BR", city: "São Paulo", lat: -23.5, lon: -46.6, region: "LATAM" },
    { code: "ID", city: "Jakarta", lat: -6.2, lon: 106.8, region: "SE-Asia" },
    { code: "KE", city: "Nairobi", lat: -1.3, lon: 36.8, region: "East Africa" },
    { code: "VN", city: "Hanoi", lat: 21.0, lon: 105.8, region: "SE-Asia" },
];

const ORIGIN = { city: "Mumbai, India", lat: 19.0, lon: 72.9 };

const project = (lat, lon) => ({ x: lon + 180, y: 90 - lat });

// Build a curved arc path between two points (geodesic-ish in 2D).
const arcPath = (a, b) => {
    const p1 = project(a.lat, a.lon);
    const p2 = project(b.lat, b.lon);
    const mx = (p1.x + p2.x) / 2;
    const my = (p1.y + p2.y) / 2;
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const curve = Math.min(30, dist * 0.35);
    // Offset perpendicular (upward in SVG space = smaller y)
    const nx = -dy / dist;
    const ny = dx / dist;
    const cx = mx + nx * curve;
    const cy = my + ny * curve - Math.min(12, dist * 0.15);
    return `M ${p1.x} ${p1.y} Q ${cx} ${cy} ${p2.x} ${p2.y}`;
};

export default function ExportMap() {
    const origin = project(ORIGIN.lat, ORIGIN.lon);

    return (
        <section
            className="bg-[#FAF9F6] py-24 md:py-32 border-b border-stone-200"
            data-testid="export-map-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
                    <div className="md:col-span-6">
                        <div className="overline mb-4">
                            <span className="inline-block w-8 h-px bg-stone-400 align-middle mr-3" />
                            Global Footprint
                        </div>
                        <h2 className="font-cabinet font-bold text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
                            From India,
                            <br />
                            <span className="italic font-normal text-[#0C4A6E]">
                                to 45+ countries.
                            </span>
                        </h2>
                    </div>
                    <div className="md:col-span-5 md:col-start-8">
                        <p className="text-base md:text-lg text-stone-700 leading-relaxed">
                            Pharmaceuticals, AYUSH medicines, cosmetics, hospital
                            equipment and turnkey hospital projects — shipped
                            from our Indian manufacturing partners to Africa,
                            MENA, CIS, LATAM, SE-Asia and beyond.
                        </p>
                    </div>
                </div>

                {/* Map */}
                <div
                    className="relative bg-white border border-stone-200 overflow-hidden"
                    data-testid="export-world-map"
                >
                    <svg
                        viewBox="0 0 360 180"
                        className="w-full h-auto block"
                        aria-label="World export map"
                    >
                        <defs>
                            <pattern
                                id="dots"
                                x="0"
                                y="0"
                                width="2"
                                height="2"
                                patternUnits="userSpaceOnUse"
                            >
                                <circle
                                    cx="1"
                                    cy="1"
                                    r="0.35"
                                    fill="#D6D3D1"
                                />
                            </pattern>
                            <linearGradient
                                id="arcGrad"
                                x1="0"
                                y1="0"
                                x2="1"
                                y2="0"
                            >
                                <stop
                                    offset="0%"
                                    stopColor="#0C4A6E"
                                    stopOpacity="0.15"
                                />
                                <stop
                                    offset="50%"
                                    stopColor="#0C4A6E"
                                    stopOpacity="1"
                                />
                                <stop
                                    offset="100%"
                                    stopColor="#4D7C0F"
                                    stopOpacity="0.9"
                                />
                            </linearGradient>
                        </defs>

                        {/* Continents (simplified stylised silhouettes, filled with dotted pattern) */}
                        <g fill="url(#dots)" stroke="#E7E5E4" strokeWidth="0.2">
                            {/* North America */}
                            <path d="M 20 35 L 30 30 L 55 28 L 75 32 L 95 40 L 100 55 L 85 70 L 70 78 L 55 75 L 45 72 L 35 60 L 25 50 Z" />
                            {/* South America */}
                            <path d="M 95 95 L 105 92 L 115 98 L 120 115 L 115 135 L 108 150 L 100 155 L 95 145 L 92 125 L 95 105 Z" />
                            {/* Europe */}
                            <path d="M 170 40 L 185 35 L 200 38 L 210 45 L 205 55 L 195 60 L 180 58 L 170 52 Z" />
                            {/* Africa */}
                            <path d="M 175 70 L 195 65 L 210 72 L 218 90 L 215 115 L 205 130 L 190 128 L 180 115 L 172 95 Z" />
                            {/* Middle East + Asia */}
                            <path d="M 210 50 L 240 45 L 275 48 L 305 55 L 315 70 L 305 85 L 285 82 L 260 78 L 235 72 L 220 68 Z" />
                            {/* India subcontinent (prominent) */}
                            <path d="M 240 75 L 255 72 L 262 82 L 258 95 L 250 100 L 245 92 Z" />
                            {/* SE Asia */}
                            <path d="M 275 90 L 290 88 L 305 92 L 315 100 L 308 108 L 295 106 L 285 100 Z" />
                            {/* Australia */}
                            <path d="M 300 130 L 320 127 L 335 132 L 338 142 L 328 150 L 310 148 L 302 140 Z" />
                        </g>

                        {/* Latitude guide lines */}
                        <g
                            stroke="#E7E5E4"
                            strokeWidth="0.15"
                            strokeDasharray="1 2"
                        >
                            <line x1="0" y1="90" x2="360" y2="90" />
                            <line x1="0" y1="66.5" x2="360" y2="66.5" />
                            <line x1="0" y1="113.5" x2="360" y2="113.5" />
                        </g>

                        {/* Arcs from India to each destination */}
                        <g fill="none" strokeLinecap="round">
                            {DESTINATIONS.map((d, i) => (
                                <path
                                    key={d.code}
                                    d={arcPath(ORIGIN, d)}
                                    stroke="url(#arcGrad)"
                                    strokeWidth="0.6"
                                    strokeDasharray="120"
                                    strokeDashoffset="120"
                                    style={{
                                        animation: `draw 2.4s ${i * 0.25}s cubic-bezier(0.2,0.7,0.2,1) forwards`,
                                    }}
                                />
                            ))}
                        </g>

                        {/* Destination pins */}
                        {DESTINATIONS.map((d, i) => {
                            const p = project(d.lat, d.lon);
                            return (
                                <g
                                    key={d.code}
                                    style={{
                                        animation: `pinFade 0.6s ${1 + i * 0.2}s ease-out forwards`,
                                        opacity: 0,
                                    }}
                                >
                                    <circle
                                        cx={p.x}
                                        cy={p.y}
                                        r="3.5"
                                        fill="#0C4A6E"
                                        fillOpacity="0.15"
                                    >
                                        <animate
                                            attributeName="r"
                                            values="3.5;6;3.5"
                                            dur="2.4s"
                                            begin={`${1 + i * 0.2}s`}
                                            repeatCount="indefinite"
                                        />
                                        <animate
                                            attributeName="fill-opacity"
                                            values="0.25;0;0.25"
                                            dur="2.4s"
                                            begin={`${1 + i * 0.2}s`}
                                            repeatCount="indefinite"
                                        />
                                    </circle>
                                    <circle
                                        cx={p.x}
                                        cy={p.y}
                                        r="1.4"
                                        fill="#0C4A6E"
                                    />
                                </g>
                            );
                        })}

                        {/* Origin pin — India (large, earth green) */}
                        <g>
                            <circle
                                cx={origin.x}
                                cy={origin.y}
                                r="5"
                                fill="#4D7C0F"
                                fillOpacity="0.2"
                            >
                                <animate
                                    attributeName="r"
                                    values="5;9;5"
                                    dur="2s"
                                    repeatCount="indefinite"
                                />
                                <animate
                                    attributeName="fill-opacity"
                                    values="0.35;0;0.35"
                                    dur="2s"
                                    repeatCount="indefinite"
                                />
                            </circle>
                            <circle
                                cx={origin.x}
                                cy={origin.y}
                                r="2.2"
                                fill="#4D7C0F"
                                stroke="#FAF9F6"
                                strokeWidth="0.5"
                            />
                        </g>
                    </svg>

                    {/* Overlay: origin + legend */}
                    <div className="absolute top-4 left-4 md:top-6 md:left-6 bg-white/95 border border-stone-200 px-4 py-3 backdrop-blur-sm">
                        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">
                            <span className="w-2 h-2 bg-[#4D7C0F] rounded-full inline-block" />
                            Origin
                        </div>
                        <div className="font-cabinet font-bold text-ink text-base leading-tight mt-0.5">
                            {ORIGIN.city}
                        </div>
                    </div>

                    <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 bg-white/95 border border-stone-200 px-4 py-3 backdrop-blur-sm flex items-center gap-3">
                        <Plane
                            size={16}
                            strokeWidth={1.5}
                            className="text-[#0C4A6E]"
                        />
                        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-600">
                            Active export lanes
                        </div>
                    </div>
                </div>

                {/* Destination chip grid */}
                <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
                    {DESTINATIONS.map((d) => (
                        <div
                            key={d.code}
                            className="flex items-center justify-between border border-stone-200 bg-white px-4 py-3"
                            data-testid={`destination-${d.code.toLowerCase()}`}
                        >
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">
                                    {d.region}
                                </div>
                                <div className="font-cabinet font-bold text-ink text-base leading-tight">
                                    {d.city}
                                </div>
                            </div>
                            <span className="w-2 h-2 bg-[#0C4A6E] rounded-full" />
                        </div>
                    ))}
                </div>
            </div>

            <style>{`
                @keyframes draw {
                    to { stroke-dashoffset: 0; }
                }
                @keyframes pinFade {
                    to { opacity: 1; }
                }
            `}</style>
        </section>
    );
}
